import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useInvitationFlowStore } from '../stores/invitationFlowStore';
import { useAcceptInvitation } from '../hooks/useAcceptInvitation';
import { useAuthStore } from '../../auth/stores/authStore';
import { clearTokenCache } from '@billda/permissions-ui';
import InvitationProcessing from './InvitationProcessing';

/**
 * Page for processing the final invitation acceptance
 * Accepts the invitation and redirects based on user role
 */
const InvitationProcessingPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Get invitation data from store
  const invitationInfo = useInvitationFlowStore(state => state.invitationInfo);
  const profileCaptured = useInvitationFlowStore(
    state => state.profileCaptured
  );
  const consentGiven = useInvitationFlowStore(state => state.consentGiven);
  const setRequiresBusinessDetails = useInvitationFlowStore(
    state => state.setRequiresBusinessDetails
  );

  // Auth store action
  const setTokens = useAuthStore(state => state.setTokens);

  // Accept invitation mutation
  const {
    mutate: acceptInvitation,
    isPending,
    isError,
    isSuccess,
  } = useAcceptInvitation();

  // Check prerequisites and redirect if needed
  useEffect(() => {
    if (!id) {
      navigate('/invitation/error', { replace: true });
      return;
    }

    if (!invitationInfo) {
      // No invitation info, redirect to loading page
      navigate(`/invitation/${id}`, { replace: true });
      return;
    }

    // Check if all required steps are completed
    const profileReady =
      !invitationInfo.requiresProfileCapture || profileCaptured;
    const consentReady =
      !invitationInfo.requiresDataSharingConsent || consentGiven;

    if (!profileReady) {
      navigate(`/invitation/${id}/profile`, { replace: true });
      return;
    }

    if (!consentReady) {
      navigate(`/invitation/${id}/consent`, { replace: true });
      return;
    }
  }, [id, invitationInfo, profileCaptured, consentGiven, navigate]);

  // Handle invitation acceptance
  useEffect(() => {
    if (isSuccess || isError || isPending) {
      return;
    }

    if (!id || !invitationInfo) {
      return;
    }

    const profileReady =
      !invitationInfo.requiresProfileCapture || profileCaptured;
    const consentReady =
      !invitationInfo.requiresDataSharingConsent || consentGiven;

    if (!profileReady || !consentReady) {
      return;
    }

    // Defer to survive Strict Mode: mount 1 schedules → cleanup cancels → mount 2 schedules → fires once
    const timer = setTimeout(() => {
      acceptInvitation(id, {
        onSuccess: response => {
          if (response?.identityToken) {
            // Clear the token cache to ensure fresh parsing of the new token
            clearTokenCache();

            // Set authentication tokens
            setTokens({
              token: response.identityToken,
              refreshToken: response.refreshToken,
            });

            // For new users (those who had to capture profile) who are booking work, redirect to business details
            // Cross-org users already have their business details
            // Admins not booking work skip business details
            if (
              invitationInfo?.requiresProfileCapture &&
              invitationInfo?.isBookingWork
            ) {
              setRequiresBusinessDetails(true);
              navigate(`/invitation/${id}/business-details`, {
                replace: true,
              });
            } else {
              navigate('/', { replace: true });
            }
          } else {
            // No tokens received, something went wrong
            navigate(`/invitation/${id}/error`);
          }
        },
        onError: () => {
          // Navigate to error page on failure
          navigate(`/invitation/${id}/error`);
        },
      });
    }, 0);

    return () => clearTimeout(timer);
  }, [
    id,
    invitationInfo,
    profileCaptured,
    consentGiven,
    isPending,
    isError,
    isSuccess,
    acceptInvitation,
    setTokens,
    setRequiresBusinessDetails,
    navigate,
  ]);

  // Show processing state
  return (
    <div className="mx-auto w-full max-w-[600px] px-4">
      <InvitationProcessing
        isAccepting={true}
        organisationName={invitationInfo?.organisationName || ''}
      />
    </div>
  );
};

export default InvitationProcessingPage;
